import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { faChevronLeft, faListCheck, faPlusCircle, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ModalService } from '../../../../core/services/modal/modal.service';
import { ProductSetApiService } from '../../../product-set/services/api/product-set-api.service';
import { ProductApiService } from '../../../product/services/api/product-api.service';
import { WebConfigService } from '../../../../core/services/web-config/web-config.service';

interface RelatedSet {
  set_sku: string;
  name: string;
  qty: number;
  price: number;
  srp_price: number;
  total_price: number;
  total_srp: number;
  selected: boolean;
  products: any[];
}

@Component({
  selector: 'app-bulk-update-tiktok-set-price',
  templateUrl: './bulk-update-tiktok-set-price.component.html',
  styleUrl: './bulk-update-tiktok-set-price.component.css'
})
export class BulkUpdateTiktokSetPriceComponent {
  faPlusCircle = faPlusCircle;
  faXmark = faXmark;
  faChevronLeft = faChevronLeft;
  faListCheck = faListCheck;

  get costPriceIndex() { return this.webConfigService.tiktokCostPriceIndex; }
  get srpPriceIndex() { return this.webConfigService.srpPriceIndex; }
  get percent() { return this.webConfigService.percenTikTokSetList; }

  form: FormGroup;

  relatedSetsBySku: { [sku: string]: RelatedSet[] } = {};
  loadingBySku: { [sku: string]: boolean } = {};

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    private ref: MatDialogRef<BulkUpdateTiktokSetPriceComponent>,
    private productSetApiService: ProductSetApiService,
    private productApiService: ProductApiService,
    private modalService: ModalService,
    private webConfigService: WebConfigService,
  ) {
    this.form = this.fb.group({
      products: this.fb.array([], [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.setDefaultProduct(this.data.set_sku);
  }

  async setDefaultProduct(setSku: string) {
    const productSetResponse: any = await this.productSetApiService.getAllTiktokSet({ set_sku: setSku });
    if (!productSetResponse.success && productSetResponse?.data?.length <= 0) {
      return;
    }

    const productSetDetials = productSetResponse?.data[0];
    const productDetials = productSetDetials?.product_info || [];
    const products = productSetDetials?.products || [];
    for (const product of productDetials) {
      if (product.prices) {
        const productMatch = products.find((item: any) => item.sku === product.sku);
        this.selectProduct(product, productMatch.qty, productMatch.srp_price, productMatch.price);
        this.loadRelatedSets(product.sku, setSku);
      }
    }
  }

  async loadRelatedSets(sku: string, currentSetSku: string) {
    this.loadingBySku[sku] = true;
    const res: any = await this.productApiService.getAllTiktokSet({
      sku,
      active: true,
      limit: 10,
      offset: 0,
    });
    this.loadingBySku[sku] = false;

    if (!res.success || !res.data?.length) {
      this.relatedSetsBySku[sku] = [];
      return;
    }

    this.relatedSetsBySku[sku] = res.data
      .filter((set: any) => set.set_sku !== currentSetSku)
      .map((set: any) => {
        const products = set.products || [];
        const line = products.find((p: any) => p.sku === sku);
        const total_srp = products.reduce(
          (sum: number, p: any) => sum + ((p.srp_price ?? 0) * (p.qty ?? 0)),
          0
        );
        return {
          set_sku: set.set_sku,
          name: set.name,
          qty: line?.qty ?? 0,
          price: line?.price ?? 0,
          srp_price: line?.srp_price ?? 0,
          total_price: set.total_price ?? 0,
          total_srp: parseFloat(total_srp.toFixed(2)),
          selected: false,
          products,
        } as RelatedSet;
      });
  }

  previewLinePrice(related: RelatedSet, sku: string): number | null {
    const productCtrl = this.products.controls.find(c => c.value.sku === sku);
    if (!productCtrl) return null;
    const { price, qty } = productCtrl.value;
    if (price === null || price === undefined || !qty) return null;
    const unitPrice = price / qty;
    return parseFloat((unitPrice * related.qty).toFixed(2));
  }

  previewTotal(related: RelatedSet, sku: string): number | null {
    const newLine = this.previewLinePrice(related, sku);
    if (newLine === null) return null;
    return parseFloat((related.total_price - related.price + newLine).toFixed(2));
  }

  previewSrp(sku: string): number | null {
    const productCtrl = this.products.controls.find(c => c.value.sku === sku);
    if (!productCtrl) return null;
    const { srp_price } = productCtrl.value;
    if (srp_price === null || srp_price === undefined) return null;
    return srp_price;
  }

  previewTotalSrp(related: RelatedSet, sku: string): number | null {
    const newSrp = this.previewSrp(sku);
    if (newSrp === null) return null;
    const next = related.total_srp - (related.srp_price * related.qty) + (newSrp * related.qty);
    return parseFloat(next.toFixed(2));
  }

  get products(): FormArray {
    return this.form.get('products') as FormArray;
  }

  selectProduct(product: any, defaultQty: number, defaultSrpPrice: number, defaultPrice: number) {
    if (!product.prices) {
      return;
    }

    const cost = product.prices[this.costPriceIndex];
    const costAdded = this.addPercentage(cost, this.percent);

    this.products.push(this.fb.group({
      sku: [product.sku],
      name: [product.name],
      qty: [defaultQty, [Validators.required, Validators.min(1)]],
      srp_price: [defaultSrpPrice ?? null, [Validators.required, Validators.min(0)]],
      price: [defaultPrice ?? null, [Validators.required, Validators.min(0)]],
      cost: [cost],
      cost_added: [costAdded],
    }));
  }

  addPercentage(value: number, percent: number): number {
    return value + (value * (percent / 100));
  }

  summaryItem() {
    return parseFloat(this.products.controls.reduce((sum, control) => sum + control.value.qty, 0).toFixed(2));
  }

  summarySRP() {
    return parseFloat(this.products.controls.reduce((sum, control) => sum + (control.value.srp_price * control.value.qty), 0).toFixed(2));
  }

  summaryCostAdded() {
    return parseFloat(this.products.controls.reduce((sum, control) => sum + (control.value.cost_added * control.value.qty), 0).toFixed(2));
  }

  summaryPrice() {
    return parseFloat(this.products.controls.reduce((sum, control) => sum + control.value.price, 0).toFixed(2));
  }

  toggleSelectAllRelated(sku: string, checked: boolean) {
    const list = this.relatedSetsBySku[sku] || [];
    list.forEach(item => (item.selected = checked));
  }

  isAllRelatedSelected(sku: string): boolean {
    const list = this.relatedSetsBySku[sku] || [];
    return list.length > 0 && list.every(item => item.selected);
  }

  countSelectedRelated(sku: string): number {
    return (this.relatedSetsBySku[sku] || []).filter(item => item.selected).length;
  }

  async propagatePriceToRelated(sku: string) {
    const productCtrl = this.products.controls.find(c => c.value.sku === sku);
    if (!productCtrl) {
      return;
    }
    const { srp_price, price, qty } = productCtrl.value;
    if (price === null || srp_price === null) {
      this.modalService.openModal('fail', 'กรุณากรอกราคาก่อน', '');
      return;
    }

    const selected = (this.relatedSetsBySku[sku] || []).filter(item => item.selected);
    if (selected.length === 0) {
      this.modalService.openModal('fail', 'ยังไม่ได้เลือกเซ็ตร่วม', '');
      return;
    }

    const unitPrice = qty > 0 ? price / qty : price;

    let successCount = 0;
    for (const set of selected) {
      const newProducts = set.products.map((p: any) => {
        if (p.sku !== sku) {
          return {
            sku: p.sku,
            qty: p.qty,
            price: p.price,
            cost: p.cost,
            srp_price: p.srp_price,
          };
        }
        return {
          sku: p.sku,
          qty: p.qty,
          price: parseFloat((unitPrice * p.qty).toFixed(2)),
          cost: p.cost,
          srp_price,
        };
      });

      const body = {
        set_sku: set.set_sku,
        products: newProducts,
        total_price: parseFloat(newProducts.reduce((sum: number, item: any) => sum + item.price, 0).toFixed(2)),
      };

      const res: any = await this.productSetApiService.updateTiktokSetPrice(body);
      if (res.success) {
        successCount++;
      }
    }

    if (successCount === selected.length) {
      this.modalService.openModal('success', 'อัพเดตเซ็ตร่วมสำเร็จ', `${successCount}/${selected.length} เซ็ต`);
    } else {
      this.modalService.openModal('fail', 'อัพเดตเซ็ตร่วมไม่สำเร็จบางรายการ', `${successCount}/${selected.length} เซ็ต`);
    }

    this.loadRelatedSets(sku, this.data.set_sku);
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const formValue: any = this.form.value;
    const body = {
      set_sku: this.data.set_sku,
      products: formValue.products.map((item: any) => ({
        sku: item.sku,
        qty: item.qty,
        price: item.price,
        cost: item.cost,
        srp_price: item.srp_price,
      })),
      total_price: parseFloat(formValue.products.reduce((sum: number, item: any) => sum + item.price, 0).toFixed(2)),
    };
    this.productSetApiService.updateTiktokSetPrice(body).then((response: any) => {
      if (response.success) {
        this.modalService.openModal('success', 'สำเร็จ', 'ปรับราคาเรียบร้อยแล้ว');
      } else {
        this.modalService.openModal('fail', 'ไม่สำเร็จ', response.message);
      }
      this.cancel();
    });
  }

  cancel() {
    this.form.reset();
    while (this.products.length) {
      this.products.removeAt(0);
    }
    this.close();
  }

  close() {
    this.ref.close({ confirm: false });
  }
}
